/* RentCastUsageTile — At-a-glance monthly RentCast API budget for
 * ispeedtoclose admins.
 *
 * Reads GET /api/admin/rentcast-usage and shows:
 *   • Current period + billing anchor day
 *   • Big usage % with color-coded urgency ring
 *   • Calls this month · Remaining · Cache hit ratio
 *   • Per-endpoint breakdown (top 3)
 *   • Budget mode (warn / hard_stop) chip
 *
 * When usage crosses 80% we tint the ring amber, past 95% crimson — so
 * Tracy sees "you're about to hit the wall" without opening the card.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Activity, AlertTriangle, RefreshCw, RotateCcw, Wifi, Zap } from "lucide-react";

export default function RentCastUsageTile() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rentcast-usage");
      setUsage(data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load usage");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/rentcast-usage/test-connection");
      setTestResult(data);
      if (data.ok) toast.success(`RentCast API is reachable · HTTP ${data.status_code} · ${data.duration_ms}ms`);
      else toast.error(`RentCast rejected the test call · HTTP ${data.status_code || "network"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test call failed");
    } finally { setTesting(false); }
  };

  const resetCounter = async () => {
    if (!window.confirm("Reset the internal RentCast usage counter to zero for the current period?\n\nCalls will resume immediately. Your real RentCast plan limit is unchanged — this only fixes a drifted internal counter.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/rentcast-usage/reset-counter");
      toast.success(data.message || "Counter reset");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !usage) {
    return (
      <div className="rounded-xl border border-[var(--line)] bg-white p-4" data-testid="rentcast-usage-tile-loading">
        <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
          <RefreshCw size={14} className="animate-spin" /> Loading RentCast usage…
        </div>
      </div>
    );
  }

  if (error && !usage) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="rentcast-usage-tile-error">
        <div className="flex items-center gap-2 font-bold mb-1"><AlertTriangle size={14} /> Couldn&apos;t load RentCast usage</div>
        <div className="text-xs opacity-90">{error}</div>
      </div>
    );
  }

  const pct = usage.usage_pct ?? 0;
  const ringColor = pct >= 95 ? "text-rose-600 border-rose-300 bg-rose-50"
                   : pct >= 80 ? "text-amber-700 border-amber-300 bg-amber-50"
                   : "text-emerald-700 border-emerald-300 bg-emerald-50";
  const modeColor = usage.mode === "hard_stop" ? "bg-rose-100 text-rose-900"
                   : usage.mode === "warn" ? "bg-amber-100 text-amber-900"
                   : "bg-slate-100 text-slate-700";

  const byEndpoint = Object.entries(usage.by_endpoint || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  return (
    <div
      className="rounded-xl border border-[var(--line)] bg-white p-4"
      data-testid="rentcast-usage-tile"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[var(--primary)]/8 grid place-items-center text-[var(--primary)]">
            <Zap size={16} />
          </div>
          <div>
            <div className="font-display font-bold text-sm">RentCast API Budget</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">
              Period {usage.period} · resets day {usage.billing_anchor_day}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${modeColor}`}
            title={
              usage.mode === "hard_stop"
                ? "hard_stop — throws an error when limit is hit"
                : usage.mode === "warn"
                ? "warn — logs a warning but keeps calling"
                : "budget mode off"
            }
            data-testid="rentcast-usage-mode-badge"
          >
            {usage.mode || "off"}
          </span>
          <button
            type="button"
            onClick={load}
            className="text-[var(--ink-3)] hover:text-[var(--ink-1)]"
            title="Refresh"
            data-testid="rentcast-usage-refresh-btn"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div
          className={`h-24 w-24 rounded-full border-4 grid place-items-center font-black text-xl ${ringColor}`}
          data-testid="rentcast-usage-pct"
        >
          {pct}%
        </div>
        <div className="flex-1">
          <Row label="Calls this month" value={usage.total_calls?.toLocaleString() ?? "—"} testid="rentcast-usage-total" />
          <Row
            label="Remaining"
            value={`${(usage.remaining ?? 0).toLocaleString()} / ${(usage.limit ?? 0).toLocaleString()}`}
            testid="rentcast-usage-remaining"
          />
          <Row
            label="Cache hit ratio"
            value={`${usage.cache_hit_ratio ?? 0}%`}
            testid="rentcast-usage-cache-ratio"
          />
        </div>
      </div>

      {pct >= 95 && (
        <div
          className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-900"
          data-testid="rentcast-usage-critical-alert"
        >
          <div className="font-bold flex items-center gap-1"><AlertTriangle size={12} /> Critical — {usage.mode === "hard_stop" ? "calls WILL start failing" : "calls close to limit"}</div>
          <div className="opacity-90 mt-1">
            Raise <code className="font-mono bg-white px-1 rounded">RENTCAST_MONTHLY_LIMIT</code>
            {" "}in production env, or set <code className="font-mono bg-white px-1 rounded">RENTCAST_BUDGET_MODE=warn</code> to keep calling.
          </div>
        </div>
      )}
      {pct >= 80 && pct < 95 && (
        <div
          className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900"
          data-testid="rentcast-usage-warning-alert"
        >
          <span className="font-bold">Approaching the wall</span> — 20% or less headroom this period.
        </div>
      )}

      {byEndpoint.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="rentcast-usage-by-endpoint">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-1.5">
            Top endpoints
          </div>
          <div className="grid grid-cols-3 gap-2">
            {byEndpoint.map(([ep, count]) => (
              <div key={ep} className="rounded bg-slate-50 border border-[var(--line)] px-2 py-1.5" data-testid={`rentcast-endpoint-${ep}`}>
                <div className="text-[9px] uppercase tracking-widest text-[var(--ink-3)] truncate" title={ep}>{ep}</div>
                <div className="font-bold text-sm mt-0.5 flex items-center gap-1">
                  <Activity size={11} className="opacity-50" /> {count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diagnostics — for when "quota exceeded" is showing but the real
          RentCast dashboard says you have plenty of room. Test Connection
          fires a real API call and returns the raw HTTP status + fix hint;
          Reset Counter zeroes the internal Mongo counter that may have
          drifted out of sync. */}
      <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="rentcast-usage-diagnostics">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] mb-2">
          Diagnostics
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={testConnection}
            disabled={testing}
            className="btn btn-outline !text-xs !py-1.5 !px-3 flex items-center gap-1"
            data-testid="rentcast-test-connection-btn"
          >
            <Wifi size={12} className={testing ? "animate-pulse" : ""} />
            {testing ? "Testing…" : "Test connection"}
          </button>
          <button
            type="button"
            onClick={resetCounter}
            disabled={resetting}
            className="btn btn-outline !text-xs !py-1.5 !px-3 flex items-center gap-1 border-amber-300 text-amber-800"
            data-testid="rentcast-reset-counter-btn"
          >
            <RotateCcw size={12} className={resetting ? "animate-spin" : ""} />
            {resetting ? "Resetting…" : "Reset internal counter"}
          </button>
        </div>
        {testResult && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-rose-50 border-rose-200 text-rose-900"
            }`}
            data-testid="rentcast-test-result"
          >
            <div className="font-bold mb-1 flex items-center justify-between">
              <span>
                {testResult.ok ? "✓ Reachable" : "✗ Rejected"} · HTTP {testResult.status_code || "network"}
              </span>
              <span className="opacity-60 text-[10px]">{testResult.duration_ms ?? "—"}ms</span>
            </div>
            <div className="opacity-90 leading-snug">
              <div><span className="opacity-70">Key preview:</span> <code className="font-mono">{testResult.api_key_first_6 || "—"}</code> · {testResult.api_key_length || 0} chars</div>
              <div className="mt-1"><span className="opacity-70">Fix:</span> {testResult.fix}</div>
              {testResult.response_snippet && (
                <details className="mt-1">
                  <summary className="cursor-pointer opacity-60 hover:opacity-100">Raw response</summary>
                  <pre className="mt-1 text-[10px] bg-white/50 p-2 rounded overflow-auto max-h-32 whitespace-pre-wrap">{testResult.response_snippet}</pre>
                </details>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-0.5" data-testid={testid}>
      <div className="text-[var(--ink-2)]">{label}</div>
      <div className="font-bold text-[var(--ink-1)]">{value}</div>
    </div>
  );
}
